import React, { useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import {
  Close,
  DateRangeOutlined,
  ErrorOutline,
  EventAvailableOutlined,
  Refresh,
  SchoolOutlined,
} from "@material-ui/icons";
import { useParent } from "../../../../context/ParentContext";
import { baseUrl } from "../../../../services/config";
import "../MonthlyStudentAttendance/parent-progress.css";
import "./announced-date-sheets.css";
import "./date-sheet-modal.css";

const getStudentId = (child) =>
  child?.student?.id || child?.student_id || child?.id || null;
const getClassId = (child) =>
  child?.student_class_id ||
  child?.student?.student_class_id ||
  child?.student?.student_class?.id ||
  null;
const getCampusId = (child) =>
  child?.campus_id || child?.student?.campus_id || child?.campus?.id || null;

function formatMonth(value) {
  if (!value) return "Schedule available";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-PK", {
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatExamDate(value) {
  if (!value) return "Date not available";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-PK", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export default function AnnouncedDateSheets() {
  const alert = useAlert();
  const { selectedChild } = useParent();
  const [sessions, setSessions] = useState([]);
  const [yearId, setYearId] = useState("");
  const [exams, setExams] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedExam, setSelectedExam] = useState(null);
  const [dateSheet, setDateSheet] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const studentId = getStudentId(selectedChild);
  const classId = getClassId(selectedChild);
  const campusId = getCampusId(selectedChild);
  const studentName = selectedChild?.student?.name || "Student";

  const headers = (json = false) => {
    const token = localStorage.getItem("parentToken");
    return {
      Accept: "application/json",
      ...(json ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const loadSessions = async () => {
    setSessionLoading(true);
    try {
      const response = await fetch(`${baseUrl}/session`, {
        headers: headers(),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Unable to load sessions.");
      const list = Array.isArray(result?.payload) ? result.payload : [];
      setSessions(list);
      const active = list.find(
        (session) => String(session.active_academic_year) === "1",
      );
      setYearId(String(active?.id || list[0]?.id || ""));
    } catch (requestError) {
      const message = requestError.message || "Unable to load sessions.";
      setError(message);
      alert.error(message);
    } finally {
      setSessionLoading(false);
    }
  };

  const loadDateSheets = async () => {
    if (!studentId || !classId || !yearId) return;
    setLoading(true);
    setError("");
    setExams([]);
    try {
      const response = await fetch(`${baseUrl}/date-sheet-exam-list`, {
        method: "POST",
        headers: headers(true),
        body: JSON.stringify({
          student_id: Number(studentId),
          year_id: Number(yearId),
          student_class_id: Number(classId),
        }),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Unable to load date sheets.");
      const payload = result?.payload || result;
      const list = Array.isArray(payload)
        ? payload.filter((exam) => Number(exam.date_sheet_status) === 1)
        : [];
      if (!list.length) {
        const message = "No announced date sheets found for this session.";
        setError(message);
        alert.show(message);
        return;
      }
      setExams(list);
    } catch (requestError) {
      const message = requestError.message || "Unable to load date sheets.";
      setError(message);
      alert.error(message);
    } finally {
      setLoading(false);
    }
  };

  const loadDateSheetDetails = async (exam) => {
    if (!campusId || !classId || !exam?.id) {
      alert.error("Student campus or class information is unavailable.");
      return;
    }
    setSelectedExam(exam);
    setDateSheet(null);
    setDetailError("");
    setDetailLoading(true);
    try {
      const response = await fetch(`${baseUrl}/exam_class_datesheet`, {
        method: "POST",
        headers: headers(true),
        body: JSON.stringify({
          campus_id: String(campusId),
          exam_id: String(exam.id),
          student_class_id: String(classId),
        }),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Unable to load the date sheet.");
      const payload = result?.payload || result;
      if (
        !payload ||
        !Array.isArray(payload.subjects) ||
        !payload.subjects.length
      ) {
        const message = "No subject schedule found for this exam.";
        setDetailError(message);
        alert.show(message);
        return;
      }
      setDateSheet(payload);
    } catch (requestError) {
      const message = requestError.message || "Unable to load the date sheet.";
      setDetailError(message);
      alert.error(message);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDateSheet = () => {
    setSelectedExam(null);
    setDateSheet(null);
    setDetailError("");
  };

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDateSheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, classId, yearId]);

  const sortedExams = useMemo(
    () =>
      [...exams].sort((a, b) =>
        String(b.month || "").localeCompare(String(a.month || "")),
      ),
    [exams],
  );

  return (
    <section className="parent-progress-page parent-date-sheet-page">
      <div className="parent-progress-hero parent-date-sheet-hero">
        <div>
          <span className="parent-progress-eyebrow">Examination schedule</span>
          <h1>Announced Date Sheets</h1>
          <p>
            {selectedChild
              ? `View announced examination schedules for ${studentName}.`
              : "Select a child to view announced date sheets."}
          </p>
        </div>
        <label className="parent-date-sheet-session">
          <span>Academic session</span>
          <select
            value={yearId}
            onChange={(event) => setYearId(event.target.value)}
            disabled={sessionLoading || !sessions.length}
          >
            <option value="">
              {sessionLoading ? "Loading sessions..." : "Select session"}
            </option>
            {sessions.map((session) => (
              <option key={session.id} value={session.id}>
                {session.year || session.name || `Session ${session.id}`}
              </option>
            ))}
          </select>
        </label>
      </div>

      {!studentId ? (
        <PageState
          icon={SchoolOutlined}
          title="No student selected"
          text="Please select a child from the top menu to view date sheets."
        />
      ) : loading ? (
        <PageState
          loading
          title="Loading date sheets"
          text="Getting the latest announced examination schedules."
        />
      ) : error ? (
        <PageState
          icon={ErrorOutline}
          error
          title="Date sheets unavailable"
          text={error}
          onRetry={yearId ? loadDateSheets : loadSessions}
        />
      ) : (
        <>
          <div className="parent-date-sheet-heading">
            <div>
              <h2>Available date sheets</h2>
              <p>Schedules announced by the school for the selected session.</p>
            </div>
            <span>{sortedExams.length} announced</span>
          </div>
          <div className="parent-date-sheet-grid">
            {sortedExams.map((exam) => (
              <button
                type="button"
                className="parent-date-sheet-card"
                key={exam.id}
                onClick={() => loadDateSheetDetails(exam)}
              >
                <div className="parent-date-sheet-icon">
                  <DateRangeOutlined />
                </div>
                <div className="parent-date-sheet-content">
                  <div className="parent-date-sheet-title">
                    <h3>
                      {exam.exam_name ||
                        exam.exam_type?.name ||
                        `Exam ${exam.id}`}
                    </h3>
                    <span>
                      <EventAvailableOutlined /> Announced
                    </span>
                  </div>
                  <p>{exam.exam_type?.name || "Examination"}</p>
                  <div className="parent-date-sheet-meta">
                    <span>
                      <small>Schedule</small>
                      <strong>{formatMonth(exam.month)}</strong>
                    </span>
                    <span>
                      <small>Session</small>
                      <strong>{exam.session?.year || yearId}</strong>
                    </span>
                    <span>
                      <small>Grade system</small>
                      <strong>{exam.grade_system || "Default"}</strong>
                    </span>
                  </div>
                </div>
                <span className="parent-date-sheet-view">View schedule</span>
              </button>
            ))}
          </div>
        </>
      )}

      {selectedExam && (
        <div
          className="parent-date-sheet-modal-backdrop"
          role="presentation"
          onMouseDown={closeDateSheet}
        >
          <section
            className="parent-date-sheet-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="date-sheet-dialog-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="parent-date-sheet-modal-header">
              <div>
                <span>Examination schedule</span>
                <h2 id="date-sheet-dialog-title">
                  {dateSheet?.exam?.exam_name ||
                    selectedExam.exam_name ||
                    "Date Sheet"}
                </h2>
                <p>
                  {dateSheet?.student_class?.name ||
                    selectedChild?.student?.student_class?.name ||
                    "Student class"}
                </p>
              </div>
              <button
                type="button"
                onClick={closeDateSheet}
                aria-label="Close date sheet"
              >
                <Close />
              </button>
            </header>
            <div className="parent-date-sheet-modal-body">
              {detailLoading ? (
                <PageState
                  loading
                  title="Loading schedule"
                  text="Getting subject dates for this examination."
                />
              ) : detailError ? (
                <PageState
                  icon={ErrorOutline}
                  error
                  title="Schedule unavailable"
                  text={detailError}
                  onRetry={() => loadDateSheetDetails(selectedExam)}
                />
              ) : dateSheet ? (
                <>
                  {dateSheet.note && (
                    <div className="parent-date-sheet-note">
                      <strong>Important note</strong>
                      <p>{dateSheet.note}</p>
                    </div>
                  )}
                  <div className="parent-date-sheet-table-wrap">
                    <table className="parent-date-sheet-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Subject</th>
                          <th>Code</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...dateSheet.subjects]
                          .sort((a, b) =>
                            String(a.date).localeCompare(String(b.date)),
                          )
                          .map((item) => (
                            <tr key={item.id}>
                              <td data-label="Date">
                                <strong>{formatExamDate(item.date)}</strong>
                              </td>
                              <td data-label="Subject">
                                {item.subject?.name || "Subject"}
                              </td>
                              <td data-label="Code">
                                {item.subject?.slug || "—"}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function PageState({ icon: Icon, title, text, loading, error, onRetry }) {
  return (
    <div
      className={`parent-date-sheet-state${error ? " error" : ""}`}
      role={error ? "alert" : "status"}
    >
      {loading ? (
        <span className="parent-date-sheet-spinner" />
      ) : (
        Icon && <Icon />
      )}
      <h2>{title}</h2>
      <p>{text}</p>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          <Refresh /> Try again
        </button>
      )}
    </div>
  );
}
